// src/components/manuscript/TemplateFields.jsx
//
// The editable fields of one manuscript structure template.
//
// Three field types, declared by the catalogue in
// `src/config/manuscriptElements.js`:
//
//   text      — one line (a title, an ISBN, a chapter number)
//   textarea  — a block (a dedication, a preface, chapter body text)
//   repeater  — a list of rows with add and remove (contents, index,
//               bibliography, glossary, reading-group questions)
//
// The repeater is new. Nothing in the codebase did list-of-rows editing before
// this, so there was nothing to reuse — if a second feature needs one, this is
// the thing to lift into `src/components/common/`, not to copy.
//
// Rows are addressed by index rather than by a generated id. That is safe here
// because a row is only ever edited, appended or removed — never reordered or
// moved between lists — and it keeps the stored shape a plain array of string
// maps, which is what the backend sanitizes and what the preview reads.
//
// The Table of Contents is the exception: its list is built from the manuscript
// (`contents`), so it is shown read-only in place of the repeater. Rows typed
// before that are left stored and untouched — not shown, not deleted.
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

import { SP, FONT, eyebrowSx } from './structureTokens';

const inputSx = {
  width: '100%',
  minWidth: 0,
  fontFamily: FONT.sans,
  fontSize: 13.5,
  color: SP.black,
  border: `1px solid ${SP.lineSoft}`,
  borderRadius: '4px',
  px: 1.5,
  py: 1.25,
  backgroundColor: SP.white,
  transition: 'border-color .2s ease, box-shadow .2s ease',
  '&:focus': {
    outline: 'none',
    borderColor: SP.purple,
    boxShadow: `0 0 0 3px ${SP.purple}1A`,
  },
};

function FieldLabel({ children, htmlFor }) {
  return (
    <Typography component="label" htmlFor={htmlFor} sx={{ ...eyebrowSx, display: 'block', mb: 0.75 }}>
      {children}
    </Typography>
  );
}

function RepeaterField({ field, rows, onChange }) {
  const setRow = (index, subKey, value) => {
    const next = rows.map((row, i) => (i === index ? { ...row, [subKey]: value } : row));
    onChange(next);
  };

  const addRow = () => {
    const blank = {};
    field.subfields.forEach((sf) => { blank[sf.key] = ''; });
    onChange([...rows, blank]);
  };

  const removeRow = (index) => onChange(rows.filter((_, i) => i !== index));

  return (
    <Box sx={{ mt: 2.5 }}>
      <FieldLabel>{field.label}</FieldLabel>

      {rows.map((row, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
          {field.subfields.map((sf) => (
            <Box
              key={sf.key}
              component="input"
              type="text"
              placeholder={sf.label}
              aria-label={`${sf.label}, ${field.singular} ${index + 1}`}
              value={row[sf.key] || ''}
              onChange={(e) => setRow(index, sf.key, e.target.value)}
              sx={{ ...inputSx, flex: 1 }}
            />
          ))}
          <Tooltip title={`Remove this ${field.singular}`}>
            <Box
              component="button"
              type="button"
              aria-label={`Remove ${field.singular} ${index + 1}`}
              onClick={() => removeRow(index)}
              sx={{
                flexShrink: 0,
                width: 30,
                height: 30,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0,
                borderRadius: '4px',
                border: `1px solid ${SP.lineSoft}`,
                backgroundColor: SP.white,
                color: SP.gray,
                cursor: 'pointer',
                transition: 'all .2s ease',
                '&:hover': { borderColor: '#B3401F', color: '#B3401F' },
              }}
            >
              <CloseIcon sx={{ fontSize: 15 }} />
            </Box>
          </Tooltip>
        </Box>
      ))}

      <Box
        component="button"
        type="button"
        onClick={addRow}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          mt: 0.25,
          px: 1.75,
          py: 1,
          fontFamily: FONT.mono,
          fontSize: 11,
          color: SP.purple,
          border: `1px dashed ${SP.purpleSoft}`,
          borderRadius: '4px',
          backgroundColor: SP.lavender,
          cursor: 'pointer',
          transition: 'all .2s ease',
          '&:hover': {
            backgroundColor: SP.purple,
            color: SP.white,
            borderStyle: 'solid',
          },
        }}
      >
        <AddIcon sx={{ fontSize: 14 }} />
        Add {field.singular}
      </Box>
    </Box>
  );
}

/** The contents list as the book will print it, without page numbers. Read-only. */
function ContentsList({ label, chapters, backMatter }) {
  const rows = [...chapters, ...backMatter];
  return (
    <Box sx={{ mt: 2.5 }}>
      <FieldLabel>{label}</FieldLabel>
      <Box component="ul" sx={{
        listStyle: 'none',
        m: 0,
        px: 1.5,
        py: 1,
        border: `1px solid ${SP.lineSoft}`,
        borderRadius: '4px',
        backgroundColor: SP.white,
      }}>
        {!chapters.length && (
          <Box component="li" sx={{ fontFamily: FONT.sans, fontSize: 13, color: SP.gray, py: 0.5 }}>
            No chapters yet. Mark a line as a chapter heading in the editor and it appears here.
          </Box>
        )}
        {rows.map((row, index) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            component="li"
            sx={{
              fontFamily: FONT.sans,
              fontSize: 13.5,
              color: SP.black,
              py: 0.5,
              pl: row.level > 1 ? 2.5 : 0,
              overflowWrap: 'anywhere',
            }}
          >
            {row.title}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function TemplateFields({ element, fields, onChangeField, contents, editorPath }) {
  return (
    <Box>
      {element.fields.map((field) => {
        const inputId = `${element.id}-${field.key}`;
        const value = fields[field.key];

        if (contents && field.type === 'repeater') {
          return (
            <ContentsList
              key={field.key}
              label={field.label}
              chapters={contents.chapters}
              backMatter={contents.backMatter}
            />
          );
        }

        if (field.type === 'repeater') {
          return (
            <RepeaterField
              key={field.key}
              field={field}
              rows={Array.isArray(value) ? value : []}
              onChange={(rows) => onChangeField(field.key, rows)}
            />
          );
        }

        return (
          <Box key={field.key} sx={{ mt: 2.5 }}>
            <FieldLabel htmlFor={inputId}>{field.label}</FieldLabel>
            <Box
              component={field.type === 'textarea' ? 'textarea' : 'input'}
              id={inputId}
              {...(field.type === 'textarea' ? {} : { type: 'text' })}
              value={value || ''}
              onChange={(e) => onChangeField(field.key, e.target.value)}
              sx={{
                ...inputSx,
                ...(field.type === 'textarea' && {
                  resize: 'vertical',
                  minHeight: 76,
                  lineHeight: 1.6,
                }),
              }}
            />
          </Box>
        );
      })}

      {element.auto && (
        <Box sx={{
          mt: 1.5,
          px: 1.75,
          py: 1.5,
          borderRadius: '4px',
          border: `1px dashed ${SP.lineSoft}`,
          backgroundColor: SP.lavender,
          fontFamily: FONT.mono,
          fontSize: 11,
          lineHeight: 1.6,
          color: SP.purple,
        }}>
          {contents ? (
            <>
              Built from the chapter headings in your manuscript, with the back matter you
              include after them. To rename or reorder a chapter, change it in the{' '}
              {editorPath ? (
                <Box component={RouterLink} to={editorPath} sx={{ color: SP.purpleDeep, fontWeight: 600 }}>
                  Manuscript Editor
                </Box>
              ) : 'Manuscript Editor'}
              . Page numbers are added when the book is printed.
            </>
          ) : 'This list is generated from your manuscript in the finished product. What you type here prints as-is for now.'}
        </Box>
      )}
    </Box>
  );
}
